import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";

import {
  ArrowLeft,
  Receipt,
  Package,
  Calendar,
  QrCode,
  AlertCircle,
  Loader2,
  CheckCircle,
  Clock,
  // Eye,
  ShoppingBag,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { orderService, type OrderDetail } from "@/services";
import { useToast } from "@/hooks/useToast";

/**
 * Página de detalle de una orden específica
 */
export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { hasRole } = useAuth();

  // Obtener el contexto de la URL
  const context = searchParams.get('context') || 'default';

  /**
   * Determina la ruta del dashboard según el rol del usuario
   */
  const getDashboardRoute = (): string => {
    // Si viene de un contexto específico, usar esa ruta
    if (context === 'student-dashboard') {
      return '/student/dashboard';
    }
    
    // Si no hay contexto, determinar por rol
    if (hasRole('Estudiante')) {
      return '/student/dashboard';
    } else if (hasRole('Apoderado')) {
      return '/dashboard';
    } else if (hasRole('Admin')) {
      return '/admin/dashboard';
    } else if (hasRole('Cajero')) {
      return '/cajero/dashboard';
    }
    
    // Por defecto, ir al dashboard del apoderado
    return '/dashboard';
  };

  /**
   * Carga el detalle de la orden al montar el componente
   */
  useEffect(() => {
    if (orderId) {
      loadOrderDetail();
    }
  }, [orderId]);

  /**
   * Carga el detalle de la orden desde el backend
   */
  const loadOrderDetail = async () => {
    if (!orderId) return;

    try {
      setIsLoading(true);
      setError(null);

      const detail = await orderService.getOrderDetail(orderId);
      setOrderDetail(detail);
    } catch (err: any) {
      console.error("Error cargando detalle de orden:", err);
      setError(err.message || "Error al cargar el detalle de la orden");
      toast({
        title: "Error",
        description: "No se pudo cargar el detalle de la orden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Formatea el precio como moneda chilena
   */
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  /**
   * Formatea la fecha en formato chileno
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Obtiene el estado y color del badge según el estado de la orden
   * Solo se usa cuando el contexto no es 'purchase-history'
   */
  const getOrderStatus = (estado: string) => {
    switch (estado.toUpperCase()) {
      case "PAGADO":
        return {
          label: "Pagado",
          variant: "default" as const,
          className: "bg-green-100 text-green-800",
          icon: CheckCircle,
        };
      case "EN_PREPARACION":
        return {
          label: "En Preparación",
          variant: "default" as const,
          className: "bg-yellow-100 text-yellow-800",
          icon: Clock,
        };
      case "LISTO_PARA_RETIRO":
        return {
          label: "Listo para Retiro",
          variant: "default" as const,
          className: "bg-blue-100 text-blue-800",
          icon: Package,
        };
      case "COMPLETADO":
        return {
          label: "Completado",
          variant: "default" as const,
          className: "bg-green-100 text-green-800",
          icon: CheckCircle,
        };
      case "CANCELADO":
        return {
          label: "Cancelado",
          variant: "default" as const,
          className: "bg-red-100 text-red-800",
          icon: AlertCircle,
        };
      default:
        return {
          label: estado,
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800",
          icon: Clock,
        };
    }
  };

  // Determinar si mostrar el estado basado en el contexto
  const shouldShowStatus = context !== 'purchase-history';

  // Estado de carga
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">
            Cargando detalle de la orden...
          </p>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error || !orderDetail) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">
              Error al cargar la orden
            </h2>
            <p className="text-red-600 mb-4">
              {error || "Orden no encontrada"}
            </p>
            <div className="space-y-2">
              <Button onClick={() => navigate(getDashboardRoute())} className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al Dashboard
              </Button>
              <Button
                onClick={loadOrderDetail}
                variant="outline"
                className="w-full"
              >
                Intentar de nuevo
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // const status = getOrderStatus(orderDetail.estado);
  // const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              onClick={() => navigate(getDashboardRoute())}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Detalle de Orden</h1>
              <p className="text-muted-foreground">ID: {orderDetail.id}</p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Información Principal */}
            <div className="md:col-span-2 space-y-6">
              {/* Resumen de la Orden */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Resumen de la Orden
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total:</span>
                      <p className="font-semibold text-lg">
                        {formatPrice(orderDetail.total)}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Productos:</span>
                      <p className="font-semibold">
                        {orderDetail.items.length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Productos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    Productos Comprados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orderDetail.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{item.productName}</p>
                            {item.productType === 'pack' && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                                Pack
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span>Cantidad: {item.quantity}</span>
                            <span>
                              Precio: {formatPrice(item.precioUnitario)}
                            </span>
                            {item.remainingQuantity !==
                              item.initialQuantity && (
                              <span className="text-orange-600">
                                Entregado:{" "}
                                {item.initialQuantity - item.remainingQuantity}/
                                {item.initialQuantity}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">
                            {formatPrice(item.subtotal)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Información Secundaria */}
            <div className="space-y-6">
              {/* Estado de la Orden - Solo mostrar si no es purchase-history */}
              {shouldShowStatus && orderDetail && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Estado de la Orden
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const status = getOrderStatus(orderDetail.estado);
                        const StatusIcon = status.icon;
                        return (
                          <>
                            <StatusIcon className="h-4 w-4" />
                            <Badge variant={status.variant} className={status.className}>
                              {status.label}
                            </Badge>
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Fechas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Fechas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Creada:
                    </span>
                    <p className="font-medium">
                      {formatDate(orderDetail.createdAt)}
                    </p>
                  </div>
                  {orderDetail.lastScanAt && (
                    <div>
                      <span className="text-sm text-muted-foreground">
                        Último escaneo:
                      </span>
                      <p className="font-medium">
                        {formatDate(orderDetail.lastScanAt)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* QR Code */}
              {orderDetail.qrCode && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <QrCode className="h-5 w-5" />
                      Código QR
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="p-4 bg-white border-2 border-gray-200 rounded-lg inline-block">
                        <QRCodeSVG
                          value={orderDetail.qrCode}
                          size={128}
                          level="H"
                          includeMargin={true}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Escaneos: {orderDetail.totalScans}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ID: {orderDetail.qrCode}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Información de Entrega */}
              {orderDetail.qrDeliveryEnabled && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Información de Entrega
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        QR Delivery:
                      </span>
                      <Badge
                        variant="default"
                        className="bg-green-100 text-green-800"
                      >
                        Habilitado
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Total escaneos:
                      </span>
                      <span className="font-medium">
                        {orderDetail.totalScans}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
